import { create } from 'zustand';
import { z } from 'zod';
import { useWalletEnvStore } from './wallet-env';
import { useApiServiceStore } from '@ewf/stores/api';
import { WorkerApi } from '@shared/types/api';

type RemoteResourcesStore = {
  isInitialized: boolean;
  intervalId: NodeJS.Timeout | undefined;
  init: () => void;
  cleanUp: () => void;
  isLoading: boolean;
  fetchData: () => Promise<void[]>;
  heroBannerSteps: HeroBannerStepProps[];
  fetchHeroBannerSteps: () => Promise<void>;
  workerCardImages: WorkerCardImages;
  fetchWorkerCardImages: () => Promise<void>;
  whitelistedSolutionGroupIds: string[];
  fetchWhitelistedSolutionGroups: () => Promise<void>;
  blacklistedWorkers: WorkerApi.WorkerId[];
  fetchBlacklistedSolutionGroups: () => Promise<void>;
};

type WorkerCardImages = Record<'small' | 'large', Record<string, string>>;

export type HeroBannerStepProps = {
  href?: string;
  image: string;
  title: {
    type: 'text' | 'gradient' | 'image' | 'breakline';
    value?: string;
  }[];
  description?: {
    type: 'text' | 'breakline';
    value?: string;
  }[];
};

export const useRemoteResourcesStore = create<RemoteResourcesStore>((set, get) => ({
  isInitialized: false,
  intervalId: undefined,
  init: async () => {
    if (get().isInitialized) return;
    set({ isInitialized: true, isLoading: true });

    get()
      .fetchData()
      .finally(() => set({ isLoading: false }));

    const TIME_IN_MINUTES = 15;
    const intervalId = setInterval(() => get().fetchData(), TIME_IN_MINUTES * 60 * 1000);
    set({ intervalId });

    useWalletEnvStore.subscribe((state, prevState) => {
      if (state.env !== prevState.env) {
        get().fetchData();
      }
    });
  },
  cleanUp: () => {
    set({ isInitialized: false });
    clearInterval(get().intervalId);
  },
  isLoading: false,
  fetchData: async () => {
    const apiIsInitialized = useApiServiceStore.getState().isInitialized;
    if (!apiIsInitialized) {
      console.error('API service is not initialized');
      return [];
    }
    return Promise.all([
      get().fetchHeroBannerSteps(),
      get().fetchWorkerCardImages(),
      get().fetchBlacklistedSolutionGroups(),
      get().fetchWhitelistedSolutionGroups(),
    ]).finally(() => set({ isLoading: false }));
  },
  heroBannerSteps: [],
  fetchHeroBannerSteps: async () => {
    const workerApi = useApiServiceStore.getState().api;
    workerApi
      .getHeroBannerUrl()
      .then((url) => fetch(url, { cache: 'no-cache' }))
      .then((response) => response.json())
      .then((data: HeroBannerStepProps[]) => {
        const fallbackStep: HeroBannerStepProps = {
          href: 'https://www.energyweb.org/energywebx',
          image: '',
          title: [
            {
              type: 'text',
              value: 'Fallback title',
            },
          ],
          description: [
            {
              type: 'text',
              value: 'Fallback description',
            },
          ],
        };
        const fileSchema = z.array(z.any());
        const { error: fileError } = fileSchema.safeParse(data);
        if (fileError) {
          console.error('Hero banner file - ', fileError);
          set({ heroBannerSteps: [fallbackStep] });
          return;
        }

        const stepSchema = z.object({
          href: z.string().optional(),
          image: z.string(),
          title: z.array(
            z.object({
              type: z.enum(['text', 'gradient', 'image', 'breakline']),
              value: z.string().optional(),
            }),
          ),
          description: z
            .array(
              z.object({
                type: z.enum(['text', 'breakline']),
                value: z.string().optional(),
              }),
            )
            .optional(),
        });
        const validatedSteps: HeroBannerStepProps[] = [];
        data.forEach((step) => {
          const { error: stepError } = stepSchema.safeParse(step);
          if (stepError) {
            console.error('Hero banner step - ', stepError);
            return;
          }
          validatedSteps.push(step);
        });
        if (validatedSteps.length === 0) {
          validatedSteps.push(fallbackStep);
        }

        set({ heroBannerSteps: validatedSteps });
      })
      .catch((err) => console.error(err));
  },
  workerCardImages: {
    small: {},
    large: {},
  },
  fetchWorkerCardImages: async () => {
    const schema = z.object({
      small: z.record(z.string(), z.string()),
      large: z.record(z.string(), z.string()),
    });
    const workerApi = useApiServiceStore.getState().api;
    workerApi
      .getWorkerCardUrl()
      .then((url) => fetch(url, { cache: 'no-cache' }))
      .then((response) => response.json())
      .then((data: WorkerCardImages) => {
        const { error } = schema.safeParse(data);
        if (error) {
          console.error('Worker card images - ', error);
          return;
        }
        set({ workerCardImages: data });
      })
      .catch((err) => console.error(err));
  },
  whitelistedSolutionGroupIds: [],
  fetchWhitelistedSolutionGroups: async () => {
    const workerApi = useApiServiceStore.getState().api;
    const schema = z.object({
      show: z.object({
        solutionGroups: z.array(z.string()),
      }),
      hide: z.object({
        solutionGroups: z.array(z.string()),
      }),
    });
    workerApi
      .getWhitelistedSolutionGroupsUrl()
      .then((url) => {
        return fetch(url, { cache: 'no-cache' });
      })
      .then((response) => response.json())
      .then((data: { show: { solutionGroups: string[] }; hide: { solutionGroups: string[] } }) => {
        const { error } = schema.safeParse(data);
        if (error) {
          console.error(error);
          return;
        }
        set({ whitelistedSolutionGroupIds: data.show.solutionGroups });
      })
      .catch((error) => {
        console.error('Failed to fetch whitelisted solution groups:', error);
        set({ whitelistedSolutionGroupIds: [] });
      });
  },
  blacklistedWorkers: [],
  fetchBlacklistedSolutionGroups: async () => {
    const schema = z.object({
      blacklistedSolutions: z.array(z.string()),
    });
    const workerApi = useApiServiceStore.getState().api;
    workerApi
      .getBlacklistedSolutionGroupsUrl()
      .then((url) => fetch(url, { cache: 'no-cache' }))
      .then((response) => response.json())
      .then((data: { blacklistedSolutions: WorkerApi.WorkerId[] }) => {
        const { error } = schema.safeParse(data);
        if (error) {
          console.error('Blacklisted workers - ', error);
          return;
        }
        set({ blacklistedWorkers: data.blacklistedSolutions });
      })
      .catch((err) => console.error(err));
  },
}));
